// recipeEdits — the shared save pipeline for user recipe edits.
//
// Both edit surfaces (RecipePageEditor's manual edits and
// RecipeReviseSheet's AI revisions) MUST route their result through
// finalizeRecipeEdits before saving, then pick a destination via
// decideSaveMode:
//
//   inPlace — the viewer owns this user_recipes row → update the
//             recipe JSONB, slug untouched (cook_logs /
//             scheduled_meals / shared links join on it; scheduled
//             history is protected by recipe_snapshot, migration 0164).
//   fork    — bundled / AI-library / community / family recipe →
//             mint a NEW row via saveRecipe with forkedFrom
//             provenance stamped, original untouched. Same shape
//             CookComplete's cook-time fork has used since the
//             feature spec ("recipe remains the golden standard").
//
// finalizeRecipeEdits re-derives everything that hangs off the edited
// text so a hand-edited recipe stays as cook-able as a generated one:
//   1. step.uses[] entries pointing at REMOVED ingredients are pruned
//      (CookMode's FOR-THIS-STEP tile reads uses[] verbatim — a stale
//      entry renders a ghost ingredient).
//   2. Steps whose instruction CHANGED and that carry no authored
//      timer/timers get inferTimersFromInstruction re-run ("simmer
//      10 minutes" typed into a step should ring).
//   3. runRecipeTechniqueChecks runs LAST over the final prose so
//      safety cues (chicken 165°F, pasta water reserve) re-apply even
//      if an edit deleted them.

import { findIngredient } from "../data/ingredients";
import { runRecipeTechniqueChecks } from "./recipeValidator";
import { inferTimersFromInstruction } from "./inferTimersFromInstruction";
import { diffRecipes, summarizeDiff, describeDiff } from "./recipeDiff";

// Version snapshot — called on every IN-PLACE content save (manual
// edit, AI revise, restore). Pushes the PRIOR recipe into a `versions`
// array on the next recipe so an edit is never destructive: the user
// can always get their original back. "make it vegan" keeps the
// omnivore version one tap away.
//
// - Snapshots strip their own nested `versions` so history doesn't
//   compound exponentially.
// - A one-line `summary` (from describeDiff) labels each entry so the
//   list reads "− mayo · + cream cheese" not just a timestamp.
// - Capped (default 20) newest-first so the JSONB row stays bounded.
// - savedAt is stamped by the caller-time clock (app code; fine).
export function applyVersionSnapshot(priorRecipe, nextRecipe, { max = 20 } = {}) {
  if (!nextRecipe || typeof nextRecipe !== "object") return nextRecipe;
  if (!priorRecipe || typeof priorRecipe !== "object") return nextRecipe;
  const { versions: priorVersions, ...priorClean } = priorRecipe;
  let summary = "Edited";
  try {
    const lines = describeDiff(diffRecipes(priorClean, nextRecipe), { max: 4 });
    if (Array.isArray(lines) && lines.length) summary = lines.join(" · ");
  } catch { /* keep the generic label */ }
  const entry = {
    recipe: priorClean,
    savedAt: new Date().toISOString(),
    summary,
    title: priorClean.title || null,
  };
  const versions = [entry, ...(Array.isArray(priorVersions) ? priorVersions : [])].slice(0, max);
  return { ...nextRecipe, versions };
}

function normalizeName(s) {
  return String(s || "").trim().toLowerCase();
}

// Identity sets for the uses[] prune — everything the FINAL draft
// still contains, by canonical id and by normalized item text.
function ingredientIdentitySets(ingredients) {
  const ids = new Set();
  const names = new Set();
  for (const ing of ingredients || []) {
    if (ing?.ingredientId) ids.add(ing.ingredientId);
    const n = normalizeName(ing?.item || ing?.name);
    if (n) names.add(n);
  }
  return { ids, names };
}

function usesEntrySurvives(use, sets) {
  if (!use) return false;
  if (use.ingredientId) return sets.ids.has(use.ingredientId);
  const n = normalizeName(use.item || use.name);
  // Untracked free-text uses with no surviving name match get pruned
  // only when we can actually identify them; unknown shapes survive
  // (pruning a row we can't identify risks dropping a real need).
  if (!n) return true;
  return sets.names.has(n);
}

/**
 * @param {object} draft    — the edited recipe (full schema.js shape)
 * @param {object} original — the pre-edit recipe (for change detection)
 * @returns {{ recipe, diagnostics, diff }}
 */
export function finalizeRecipeEdits(draft, original) {
  if (!draft) return { recipe: draft, diagnostics: null, diff: null };
  const next = { ...draft };

  // 1. uses[] prune against the final ingredient set.
  const sets = ingredientIdentitySets(next.ingredients);
  if (Array.isArray(next.steps)) {
    next.steps = next.steps.map((step) => {
      if (!Array.isArray(step?.uses) || step.uses.length === 0) return step;
      const kept = step.uses.filter((u) => usesEntrySurvives(u, sets));
      return kept.length === step.uses.length ? step : { ...step, uses: kept };
    });
  }

  // 2. Timer inference for CHANGED instructions on timer-less steps.
  //    Authored timers always win; unchanged steps are never touched
  //    (re-inference on stable prose could only churn).
  const originalSteps = Array.isArray(original?.steps) ? original.steps : [];
  const origByKey = new Map(originalSteps.map((s, i) => [s?.id || `idx:${i}`, s]));
  if (Array.isArray(next.steps)) {
    next.steps = next.steps.map((step, i) => {
      if (!step || step.timer != null || (Array.isArray(step.timers) && step.timers.length > 0)) {
        return step;
      }
      const orig = origByKey.get(step.id || `idx:${i}`);
      const changed = !orig
        || String(orig.instruction || "").trim() !== String(step.instruction || "").trim();
      if (!changed) return step;
      const inferred = inferTimersFromInstruction(step.instruction);
      return inferred ? { ...step, ...inferred } : step;
    });
  }

  // 3. Technique validation last — sees the final prose, auto-fixes
  //    safety cues, returns diagnostics for an optional badge.
  let finalRecipe = next;
  let diagnostics = null;
  try {
    const checked = runRecipeTechniqueChecks(next, { findIngredient });
    finalRecipe = checked.recipe || next;
    diagnostics = checked.diagnostics || null;
  } catch (e) {
    // Validation must never block a save — it's a quality layer.
    // eslint-disable-next-line no-console
    console.warn("[recipeEdits] technique validate threw:", e?.message || e);
  }

  const diff = diffRecipes(original, finalRecipe);
  return { recipe: finalRecipe, diagnostics, diff };
}

/**
 * Smart-hybrid ownership decision. A row the viewer owns in
 * user_recipes edits in place; everything else (bundled recipe with
 * no row, family/community recipe owned by someone else) forks.
 *
 * @param {object|null} bookRow — { id, userId, ... } user_recipes row, or null
 * @param {string} viewerId
 * @returns {"inPlace" | "fork"}
 */
export function decideSaveMode({ bookRow, viewerId }) {
  return bookRow?.id && bookRow.userId === viewerId ? "inPlace" : "fork";
}

/**
 * Fork payload — strips the slug (saveRecipe mints a fresh unique
 * one) and stamps forkedFrom provenance + compact tweak counts. Same
 * provenance shape CookComplete's cook-time fork writes, extended
 * with `tweaks` so the recipe page can celebrate the customization
 * without retaining the whole original.
 */
export function buildForkPayload(finalRecipe, sourceRow, { title = null, diff = null } = {}) {
  const sourceRecipe = sourceRow?.recipe || sourceRow || {};
  return {
    ...finalRecipe,
    title: String(title || "").trim()
      || finalRecipe.title
      || `${sourceRecipe.title || "Recipe"} v2`,
    slug: undefined,
    forkedFrom: {
      slug:   sourceRow?.slug || sourceRecipe.slug || null,
      title:  sourceRecipe.title || null,
      source: sourceRow?.source || sourceRecipe.source || null,
      tweaks: summarizeDiff(diff),
    },
  };
}
